/**
 * Chat Export Utilities
 *
 * Platform-agnostic helpers for building export options and formatting preview content.
 * Used by ChatManagementModal for both Electron and Web platforms.
 */

import {
    ExportFormat,
    ExportOptions,
    ExportTheme,
    ExportConfig,
} from '../types/models';

/**
 * Generates header and footer text for export documents.
 * Clamps title to 50 characters to prevent overflow in exports.
 */
const generateExportTexts = (chatId?: string, chatTitle?: string) => ({
    headerText: `EdgeRunner : Chat Export - (${new Date().toLocaleDateString()})`,
    footerText: chatId ? `Chat ID: ${chatId}` : '',
    // Clamp title to reasonable character limit (50 chars for exports)
    clampedTitle: chatTitle && chatTitle.length > 50 
        ? `${chatTitle.substring(0, 47)}...` 
        : chatTitle
});

/**
 * Builds a complete ExportOptions object from individual settings.
 *
 * @param format - Export format (pdf, markdown, txt)
 * @param theme - Theme for PDF exports (light, dark)
 * @param model - Model name to display in export
 * @param chatId - Optional chat ID for footer text
 * @param configOverrides - Optional config overrides
 * @param chatTitle - Optional chat title for document title
 * @returns Complete ExportOptions object ready for IExportService
 */
export const buildExportOptions = (
    format: ExportFormat,
    theme: ExportTheme,
    model: string,
    chatId?: string,
    configOverrides?: Partial<ExportConfig>,
    chatTitle?: string,
): ExportOptions => {
    // Note : Currently the only place where headerText and footerText are set
    // TODO : Refactor to allow configuration from user settings
    const { headerText, footerText, clampedTitle } = generateExportTexts(chatId, chatTitle);

    // Use defaults from central schema (single source of truth)
    const defaultConfig: ExportConfig = {
        // Content options
        includeMarkdown: true,
        includeCitations: true,
        includeReasoning: true,
        includeMetadata: true,
        includeTimestamp: false,
        // Display options  
        showModel: true,
        showTitle: true,
        showRoleHeader: true,
        // Layout options
        includeHeader: true,
        includeFooter: true,
        // Image options
        includeImages: true,
        embedImages: true,
        imageQuality: 100,
    };

    // Keep reference stable when overrides provided (prevents update loops in SettingsInputs)
    const config: ExportConfig =
        (configOverrides as ExportConfig) || defaultConfig;

    const effectiveTheme: ExportTheme = theme;

    const baseOptions: ExportOptions = {
        format,
        config,
        headerText,
        footerText,
        title: clampedTitle,
        model,
        theme: effectiveTheme,
    };

    if (format === 'pdf') {
        return {
            ...baseOptions,
            pageSize: 'A4',
            landscape: false,
            margins: 'Default',
        };
    }

    return baseOptions;
};

/**
 * Wraps export content in HTML for preview display in an iframe.
 *
 * Injects preview-specific styles (scrollbars, full-height) into the <head>.
 * For non-PDF formats, wraps plain content in an HTML shell with <pre>.
 *
 * @param content - Raw export content (HTML for PDF, plain text for others)
 * @param theme - Theme for styling (light, dark)
 * @param format - Export format to determine wrapping behavior
 * @returns Complete HTML document string for iframe src
 */
export const wrapPreviewHtml = (
    content: string,
    theme: ExportTheme,
    format: ExportFormat,
): string => {
    const iframeCss = `
        html, body { margin: 0; padding: 0; overflow: hidden; overscroll-behavior: contain; }
        ::-webkit-scrollbar { width: 12px; height: 12px; }
        ::-webkit-scrollbar-track { background: #0e0e10; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: #4a4950; border-radius: 10px; border: 2px solid #0e0e10; }
        ::-webkit-scrollbar-thumb:hover { background: #c4c4c8; }
        body { background: ${theme === 'dark' ? '#202025' : '#ffffff'}; color: ${theme === 'dark' ? '#EDEDED' : '#27262B'}; }
        pre { margin: 0; padding: 12px; font-family: 'Roboto Mono', 'Monaco', 'Menlo', monospace; white-space: pre-wrap; line-height: 1.5; overflow: visible; }
    `.trim();

    const injectHeadStyle = (html: string) =>
        html.includes('</head>')
            ? html.replace('</head>', `<style>${iframeCss}</style></head>`)
            : `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${iframeCss}</style></head><body>${html}</body></html>`;

    if (format === 'pdf') {
        return injectHeadStyle(content);
    }

    // Escape text for safe <pre> rendering
    const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const body = `<pre>${escapeHtml(content || '')}</pre>`;
    return injectHeadStyle(body);
};

const EXPORT_EXTENSION_MAP: Record<ExportFormat, string> = {
    pdf: 'pdf',
    markdown: 'md',
    txt: 'txt',
    docx: 'docx',
    magazine: 'mag',
};

/**
 * Builds a structured export filename.
 * Returns both the base name (for use as document title) and the full filename with extension.
 * Format: `chat-{chatId}-{YYYY-MM-DD}.{ext}` — mirrors the Electron bulk export pattern.
 */
export function buildExportFilename(
    chatId: string | undefined,
    format: ExportFormat,
): { base: string; full: string } {
    const date = new Date().toISOString().split('T')[0];
    const base = chatId ? `chat-${chatId}-${date}` : `chat-export-${date}`;
    return { base, full: `${base}.${EXPORT_EXTENSION_MAP[format]}` };
}
